import React, { useState, useEffect } from 'react';
import {
  IconButton,
  Badge,
  Popover,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Button,
  Divider,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import axios from '../utils/axios';
import { useNavigate } from 'react-router-dom';

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const navigate = useNavigate();

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const [notificationsRes, countRes] = await Promise.all([
        axios.get('/api/notifications'),
        axios.get('/api/notifications/unread/count')
      ]);
      // Only show unread notifications
      const unreadNotifications = notificationsRes.data.filter(n => !n.read);
      setNotifications(unreadNotifications);
      setUnreadCount(countRes.data.count);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await axios.patch(`/api/notifications/${notificationId}/read`);
      // Remove the notification from the list (only show unread)
      setNotifications(notifications.filter(n => n._id !== notificationId));
      setUnreadCount(Math.max(0, unreadCount - 1));
      // Refresh to get updated count
      const countRes = await axios.get('/api/notifications/unread/count');
      setUnreadCount(countRes.data.count);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleConfirmBooking = async (notification) => {
    try {
      const bookingId = notification.bookingId?._id || notification.bookingId;
      if (bookingId) {
        await axios.patch(`/api/bookings/${bookingId}/status`, { 
          status: 'confirmed' 
        });
        // Mark notification as read on backend
        if (notification._id) {
          try {
            await axios.patch(`/api/notifications/${notification._id}/read`);
          } catch (readError) {
            console.error('Error marking notification as read:', readError);
          }
        }
        // Remove notification from list (mark as read and remove)
        setNotifications(notifications.filter(n => n._id !== notification._id));
        // Refresh unread count
        const countRes = await axios.get('/api/notifications/unread/count');
        setUnreadCount(countRes.data.count);
      }
    } catch (error) {
      console.error('Error confirming booking:', error);
      alert('Failed to confirm booking. Please try again.');
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      handleMarkAsRead(notification._id);
    }
    
    // Navigate based on notification type
    if (notification.type === 'booking_request') {
      navigate('/received-bookings');
    } else if (notification.type === 'booking_confirmed') {
      navigate('/bookings');
    }
    handleClose();
  };

  const handleClearAll = async () => {
    setClearDialogOpen(true);
  };

  const handleConfirmClear = async () => {
    try {
      await axios.delete('/api/notifications');
      // Clear local state
      setNotifications([]);
      setUnreadCount(0);
      // Refresh to confirm
      await fetchNotifications();
      setClearDialogOpen(false);
    } catch (error) {
      console.error('Error clearing notifications:', error);
      alert('Failed to clear notifications. Please try again.');
      setClearDialogOpen(false);
    }
  };

  const handleCancelClear = () => {
    setClearDialogOpen(false);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'notification-popover' : undefined;

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleClick}
        sx={{ ml: 1 }}
      >
        {unreadCount > 0 ? (
          <Badge badgeContent={unreadCount} color="error">
            <NotificationsIcon />
          </Badge>
        ) : (
          <NotificationsIcon />
        )}
      </IconButton>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 400,
            maxHeight: 600,
            mt: 1
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6">
              Notifications
            </Typography>
            {notifications.length > 0 && (
              <Button
                size="small"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClearAll();
                }}
                sx={{ 
                  minWidth: 'auto', 
                  px: 1.5,
                  py: 0.5,
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'transparent',
                  minHeight: '36px' // Better touch target for mobile
                }}
              >
                Clear All
              </Button>
            )}
          </Box>
          <Divider sx={{ mb: 1 }} />
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : notifications.length === 0 ? (
            <Alert severity="info">No notifications</Alert>
          ) : (
            <List sx={{ maxHeight: 500, overflow: 'auto' }}>
              {notifications.map((notification) => (
                <ListItem
                  key={notification._id}
                  disablePadding
                  sx={{
                    backgroundColor: notification.read ? 'transparent' : 'action.hover',
                    borderLeft: notification.read ? 'none' : '3px solid',
                    borderColor: 'primary.main',
                    mb: 0.5
                  }}
                >
                  <ListItemButton
                    onClick={() => handleNotificationClick(notification)}
                    sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 1.5 }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="subtitle2" fontWeight={notification.read ? 400 : 600}>
                          {notification.title}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {notification.message}
                          </Typography>
                          {notification.type === 'booking_request' && !notification.read && (
                            <Box sx={{ mt: 1, width: '100%' }}>
                              <Button
                                variant="contained"
                                color="success"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleConfirmBooking(notification);
                                }}
                                sx={{ mr: 1 }}
                              >
                                Confirm Booking
                              </Button>
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Handle cancel booking
                                  const bookingId = notification.bookingId?._id || notification.bookingId;
                                  if (bookingId && window.confirm('Are you sure you want to cancel this booking?')) {
                                    axios.patch(`/api/bookings/${bookingId}/status`, { 
                                      status: 'cancelled' 
                                    }).then(async () => {
                                      // Mark notification as read on backend
                                      if (notification._id) {
                                        try {
                                          await axios.patch(`/api/notifications/${notification._id}/read`);
                                        } catch (readError) {
                                          console.error('Error marking notification as read:', readError);
                                        }
                                      }
                                      // Remove notification from list
                                      setNotifications(notifications.filter(n => n._id !== notification._id));
                                      // Refresh unread count
                                      const countRes = await axios.get('/api/notifications/unread/count');
                                      setUnreadCount(countRes.data.count);
                                    }).catch(err => {
                                      console.error('Error cancelling booking:', err);
                                      alert('Failed to cancel booking. Please try again.');
                                    });
                                  }
                                }}
                              >
                                Cancel
                              </Button>
                            </Box>
                          )}
                        </>
                      }
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                      {new Date(notification.createdAt).toLocaleString()}
                    </Typography>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Popover>
      <Dialog
        open={clearDialogOpen}
        onClose={handleCancelClear}
        aria-labelledby="clear-dialog-title"
        aria-describedby="clear-dialog-description"
      >
        <DialogTitle id="clear-dialog-title">
          Clear All Notifications?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="clear-dialog-description">
            Are you sure you want to permanently clear all notifications? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelClear} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmClear} color="error" variant="contained" autoFocus>
            Clear All
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NotificationBell;

